import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useState } from "react";

import {
  updateMobileNumber,
  clearMobileNumber,
} from "../../../services/userServices";

import styles from "./index.module.scss";

interface IMobileNumberModal {
  action: "update" | "clear" | null;
  currentMobile?: string;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function MobileNumberModal({
  action,
  currentMobile,
  onClose,
  onSuccess,
  isVisible = false,
}: IMobileNumberModal) {
  const { userId } = useParams();
  const [countryCode, setCountryCode] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const updateMutation = useMutation({
    mutationFn: ({
      userId,
      countryCode,
      mobileNumber,
    }: {
      userId: string;
      countryCode: string;
      mobileNumber: string;
    }) => updateMobileNumber(userId, countryCode, mobileNumber),
    onSuccess: () => {
      toast.success("Mobile number updated successfully");
      onSuccess();
      onClose();
      setCountryCode("");
      setMobileNumber("");
    },
    onError: (error: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${error.message}, userId: ${userId}`
      );
    },
  });

  const clearMutation = useMutation({
    mutationFn: (userId: string) => clearMobileNumber(userId),
    onSuccess: () => {
      toast.success("Mobile number cleared successfully");
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${error.message}, userId: ${userId}`
      );
    },
  });

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numeric digits
    if (value === "" || /^\d+$/.test(value)) {
      setCountryCode(value);
    }
  };

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numeric digits
    if (value === "" || /^\d+$/.test(value)) {
      setMobileNumber(value);
    }
  };

  const handleSubmit = async () => {
    if (action === "update") {
      if (!countryCode.trim() || !mobileNumber.trim()) {
        toast.error("Please enter both country code and mobile number");
        return;
      }
      updateMutation.mutate({
        userId: userId || "",
        countryCode,
        mobileNumber,
      });
    } else if (action === "clear") {
      clearMutation.mutate(userId || "");
    }
  };

  const handleClose = () => {
    setCountryCode("");
    setMobileNumber("");
    onClose();
  };

  return (
    <Modal
      id="mobileNumberModal"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={isVisible}
      onHide={handleClose}
    >
      <Modal.Body>
        <div className={styles.modalContainer}>
          {action === "update" && (
            <>
              <h2 className={styles.modalTitle}>Update mobile number</h2>
              <p className={styles.modalDescription}>
                Current mobile: {currentMobile}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>New mobile number</Form.Label>
                <Row>
                  <Col xs={4}>
                    <Form.Control
                      type="text"
                      placeholder="Country code"
                      value={countryCode}
                      onChange={handleCountryCodeChange}
                      autoFocus
                    />
                    <Form.Text className="text-muted">e.g., 65</Form.Text>
                  </Col>
                  <Col xs={8}>
                    <Form.Control
                      type="text"
                      placeholder="Mobile number"
                      value={mobileNumber}
                      onChange={handleMobileNumberChange}
                    />
                    <Form.Text className="text-muted">e.g., 12345678</Form.Text>
                  </Col>
                </Row>
              </Form.Group>
            </>
          )}

          {action === "clear" && (
            <>
              <h2 className={styles.modalTitle}>Clear mobile number</h2>
              <p className={styles.modalDescription}>
                Are you sure you want to clear the mobile number?
              </p>
              <p className={styles.accountInfo}>Current: {currentMobile}</p>
              <p className={styles.modalWarning}>
                This action will remove the mobile number from the user's
                account.
              </p>
            </>
          )}

          <div className={styles.modalFooter}>
            <Button
              className={styles.submitButton}
              variant="success"
              size="lg"
              onClick={handleSubmit}
              disabled={updateMutation.isLoading || clearMutation.isLoading}
            >
              {updateMutation.isLoading || clearMutation.isLoading
                ? "Processing..."
                : action === "update"
                ? "Update"
                : "Clear"}
            </Button>
            <Button
              className={styles.cancelButton}
              variant="secondary"
              size="lg"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
